import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  BuildingOfficeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import api from '../../services/api';
import axios from 'axios'; // TODO: rimuovere, usare api
import API_CONFIG from '../../config/api';

const GestioneGaranti = ({ pratica, ruoloUtente, onGarantiAggiornati }) => {
  const [garanti, setGaranti] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingGarante, setEditingGarante] = useState(null);
  const [formData, setFormData] = useState({
    tipoGarante: 'persona_fisica',
    nome: '',
    cognome: '',
    codiceFiscale: '',
    dataNascita: '',
    luogoNascita: '',
    ragioneSociale: '',
    partitaIva: '',
    email: '',
    telefono: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    professione: '',
    redditoAnnuo: '',
    tipoContratto: '',
    note: ''
  });

  useEffect(() => {
    if (pratica?.id) {
      fetchGaranti();
    }
  }, [pratica?.id]);

  const fetchGaranti = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/pratica/${pratica.id}/garanti`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setGaranti(response.data.garanti || []);
      }
    } catch (error) {
      console.error('Errore nel caricamento garanti:', error);
      toast.error('Errore nel caricamento dei garanti');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      tipoGarante: 'persona_fisica',
      nome: '',
      cognome: '',
      codiceFiscale: '',
      dataNascita: '',
      luogoNascita: '',
      ragioneSociale: '',
      partitaIva: '',
      email: '',
      telefono: '',
      indirizzo: '',
      citta: '',
      cap: '',
      provincia: '',
      professione: '',
      redditoAnnuo: '',
      tipoContratto: '',
      note: ''
    });
    setEditingGarante(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingGarante 
        ? `/pratica/${pratica.id}/garanti/${editingGarante.id}`
        : `/pratica/${pratica.id}/garanti`;
      
      const method = editingGarante ? 'PUT' : 'POST';
      
      const response = await api({
        method,
        url,
        data: formData,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        resetForm();
        fetchGaranti();
        if (onGarantiAggiornati) onGarantiAggiornati();
      }
    } catch (error) {
      console.error('Errore nel salvataggio garante:', error);
      toast.error(error.response?.data?.error || 'Errore nel salvataggio del garante');
    }
  };

  const handleEdit = (garante) => {
    setFormData({
      tipoGarante: garante.tipoGarante || 'persona_fisica',
      nome: garante.nome || '',
      cognome: garante.cognome || '',
      codiceFiscale: garante.codiceFiscale || '',
      dataNascita: garante.dataNascita || '',
      luogoNascita: garante.luogoNascita || '',
      ragioneSociale: garante.ragioneSociale || '',
      partitaIva: garante.partitaIva || '',
      email: garante.email || '',
      telefono: garante.telefono || '',
      indirizzo: garante.indirizzo || '',
      citta: garante.citta || '',
      cap: garante.cap || '',
      provincia: garante.provincia || '',
      professione: garante.professione || '',
      redditoAnnuo: garante.redditoAnnuo || '',
      tipoContratto: garante.tipoContratto || '',
      note: garante.note || ''
    });
    setEditingGarante(garante);
    setShowForm(true);
  };

  const handleDelete = async (garanteId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo garante?')) {
      return;
    }

    try {
      const response = await api.delete(`/pratica/${pratica.id}/garanti/${garanteId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        toast.success('Garante eliminato con successo');
        fetchGaranti();
        if (onGarantiAggiornati) onGarantiAggiornati();
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione garante:', error);
      toast.error('Errore nell\'eliminazione del garante');
    }
  };

  const handleVerifica = async (garanteId, statoVerifica) => {
    try {
      const response = await api.put(`/pratica/${pratica.id}/garanti/${garanteId}/verifica`, {
        statoVerifica
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        toast.success(response.data.message);
        fetchGaranti();
        if (onGarantiAggiornati) onGarantiAggiornati();
      }
    } catch (error) {
      console.error('Errore nella verifica garante:', error);
      toast.error('Errore nella verifica del garante');
    }
  };

  const canManageGaranti = () => {
    return (ruoloUtente === 'agente' || ruoloUtente === 'agente_commerciale') && 
           ['attesa_documenti_cliente', 'richiesta_integrazione', 'in_lavorazione', 'pratica_avviata'].includes(pratica.statoPratica);
  };

  const canVerifyGaranti = () => {
    return ruoloUtente === 'admin';
  };

  const getStatoVerificaColor = (stato) => {
    switch (stato) {
      case 'verificato':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'respinto':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    }
  };

  const getStatoVerificaIcon = (stato) => {
    switch (stato) {
      case 'verificato':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'respinto':
        return <XCircleIcon className="h-4 w-4" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4" />;
    }
  };

  if (!pratica) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      <div className="px-6 py-4 border-b bg-gray-50">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <UserIcon className="h-5 w-5 mr-2 text-blue-600" />
              Figure Garanti
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Gestione delle figure garanti associate alla pratica
            </p>
          </div>
          {canManageGaranti() && (
            <button
              onClick={() => setShowForm(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Aggiungi Garante
            </button>
          )}
        </div>
      </div>

      <div className="p-6">
        {/* Form per aggiungere/modificare garante */}
        {showForm && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
            <h4 className="text-lg font-medium text-gray-900 mb-4">
              {editingGarante ? 'Modifica Garante' : 'Nuovo Garante'}
            </h4>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Tipo Garante */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Garante *
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="tipoGarante"
                      value="persona_fisica"
                      checked={formData.tipoGarante === 'persona_fisica'}
                      onChange={(e) => setFormData({...formData, tipoGarante: e.target.value})}
                      className="mr-2"
                    />
                    <UserIcon className="h-4 w-4 mr-1" />
                    Persona Fisica
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="tipoGarante"
                      value="persona_giuridica"
                      checked={formData.tipoGarante === 'persona_giuridica'}
                      onChange={(e) => setFormData({...formData, tipoGarante: e.target.value})}
                      className="mr-2"
                    />
                    <BuildingOfficeIcon className="h-4 w-4 mr-1" />
                    Persona Giuridica
                  </label>
                </div>
              </div>

              {/* Campi per Persona Fisica */}
              {formData.tipoGarante === 'persona_fisica' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                    <input
                      type="text"
                      value={formData.nome}
                      onChange={(e) => setFormData({...formData, nome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cognome *</label>
                    <input
                      type="text"
                      value={formData.cognome}
                      onChange={(e) => setFormData({...formData, cognome: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Codice Fiscale *</label>
                    <input
                      type="text"
                      value={formData.codiceFiscale}
                      onChange={(e) => setFormData({...formData, codiceFiscale: e.target.value.toUpperCase()})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength="16"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data di Nascita</label>
                    <input
                      type="date"
                      value={formData.dataNascita}
                      onChange={(e) => setFormData({...formData, dataNascita: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Luogo di Nascita</label>
                    <input
                      type="text"
                      value={formData.luogoNascita}
                      onChange={(e) => setFormData({...formData, luogoNascita: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Campi per Persona Giuridica */}
              {formData.tipoGarante === 'persona_giuridica' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ragione Sociale *</label>
                    <input
                      type="text"
                      value={formData.ragioneSociale}
                      onChange={(e) => setFormData({...formData, ragioneSociale: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Partita IVA *</label>
                    <input
                      type="text"
                      value={formData.partitaIva}
                      onChange={(e) => setFormData({...formData, partitaIva: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      maxLength="11"
                      required
                    />
                  </div>
                </div>
              )}

              {/* Campi comuni */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefono</label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({...formData, telefono: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Indirizzo</label>
                  <input
                    type="text"
                    value={formData.indirizzo}
                    onChange={(e) => setFormData({...formData, indirizzo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Città</label>
                  <input
                    type="text"
                    value={formData.citta}
                    onChange={(e) => setFormData({...formData, citta: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">CAP</label>
                  <input
                    type="text"
                    value={formData.cap}
                    onChange={(e) => setFormData({...formData, cap: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="5"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Provincia</label>
                  <input
                    type="text"
                    value={formData.provincia}
                    onChange={(e) => setFormData({...formData, provincia: e.target.value.toUpperCase()})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    maxLength="2"
                  />
                </div>
              </div>

              {/* Campi professionali/finanziari */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Professione</label>
                  <input
                    type="text"
                    value={formData.professione}
                    onChange={(e) => setFormData({...formData, professione: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Reddito Annuo (€)</label>
                  <input
                    type="number"
                    value={formData.redditoAnnuo}
                    onChange={(e) => setFormData({...formData, redditoAnnuo: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Contratto</label>
                  <select
                    value={formData.tipoContratto}
                    onChange={(e) => setFormData({...formData, tipoContratto: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Seleziona...</option>
                    <option value="tempo_indeterminato">Tempo Indeterminato</option>
                    <option value="tempo_determinato">Tempo Determinato</option>
                    <option value="pensionato">Pensionato</option>
                    <option value="libero_professionista">Libero Professionista</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({...formData, note: e.target.value})}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Note aggiuntive sul garante..."
                />
              </div>

              {/* Pulsanti */}
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingGarante ? 'Aggiorna' : 'Salva'} Garante
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Lista garanti */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-500">Caricamento garanti...</p>
          </div>
        ) : garanti.length > 0 ? (
          <div className="space-y-4">
            {garanti.map((garante) => (
              <div key={garante.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      {garante.tipoGarante === 'persona_fisica' ? (
                        <UserIcon className="h-5 w-5 text-blue-600" />
                      ) : (
                        <BuildingOfficeIcon className="h-5 w-5 text-blue-600" />
                      )}
                      <h4 className="font-medium text-gray-900">
                        {garante.tipoGarante === 'persona_fisica' 
                          ? `${garante.nome} ${garante.cognome}`
                          : garante.ragioneSociale
                        }
                      </h4>
                      <span className={`px-2 py-1 text-xs rounded-full border flex items-center space-x-1 ${getStatoVerificaColor(garante.statoVerifica)}`}>
                        {getStatoVerificaIcon(garante.statoVerifica)}
                        <span className="capitalize">{garante.statoVerifica.replace('_', ' ')}</span>
                      </span>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600">
                      {garante.tipoGarante === 'persona_fisica' ? (
                        <>
                          <div>CF: {garante.codiceFiscale}</div>
                          {garante.dataNascita && <div>Nato il: {new Date(garante.dataNascita).toLocaleDateString()}</div>}
                        </>
                      ) : (
                        <>
                          <div>P.IVA: {garante.partitaIva}</div>
                        </>
                      )}
                      {garante.email && <div>Email: {garante.email}</div>}
                      {garante.telefono && <div>Tel: {garante.telefono}</div>}
                      {garante.professione && <div>Professione: {garante.professione}</div>}
                      {garante.redditoAnnuo && <div>Reddito: €{parseFloat(garante.redditoAnnuo).toLocaleString()}</div>}
                    </div>
                    
                    {garante.note && (
                      <div className="mt-2 text-sm text-gray-600">
                        <strong>Note:</strong> {garante.note}
                      </div>
                    )}
                    
                    {garante.verificatore && (
                      <div className="mt-2 text-xs text-gray-500">
                        Verificato da: {garante.verificatore.nome} {garante.verificatore.cognome} 
                        {garante.dataVerifica && ` il ${new Date(garante.dataVerifica).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {canVerifyGaranti() && garante.statoVerifica === 'da_verificare' && (
                      <>
                        <button
                          onClick={() => handleVerifica(garante.id, 'verificato')}
                          className="p-2 text-green-600 hover:bg-green-50 rounded"
                          title="Verifica garante"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleVerifica(garante.id, 'respinto')}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Respingi garante"
                        >
                          <XCircleIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                    
                    {canManageGaranti() && (
                      <>
                        <button
                          onClick={() => handleEdit(garante)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                          title="Modifica garante"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(garante.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded"
                          title="Elimina garante"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <UserIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Nessun garante presente</h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              {canManageGaranti()
                ? 'Aggiungi una figura garante per questa pratica se necessario.'
                : 'Non sono stati ancora aggiunti garanti per questa pratica.'}
            </p>
            {canManageGaranti() && (
              <button
                onClick={() => setShowForm(true)}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center mx-auto"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Aggiungi primo garante
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestioneGaranti; 